import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import placementTestService from '../../services/placementTestService';
import './WelcomeTrialModal.css';

interface WelcomeTrialModalProps {
  onClose: () => void;
}

/**
 * One-time welcome modal shown right after a new account is created.
 * Strategy varies by whether the user already took the placement test:
 *   - new user, no placement: CTA → /placement-test (recommend it)
 *   - new user, already placed (took test before registering): CTA → /
 *     ("Show me my recommended course")
 *
 * The "already placed" branch matters because the placement test is
 * @Public() and many people take it BEFORE creating an account, then
 * register only afterwards. Sending them back to the test would be rude.
 */
const WelcomeTrialModal: React.FC<WelcomeTrialModalProps> = ({ onClose }) => {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const [hasPlacement, setHasPlacement] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    placementTestService
      .getStatus()
      .then((status) => {
        if (cancelled) return;
        setHasPlacement(status.hasCompleted);
      })
      .catch(() => {
        if (cancelled) return;
        setHasPlacement(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePrimary = () => {
    onClose();
    if (hasPlacement) {
      navigate('/');
    } else {
      navigate('/placement-test');
    }
  };

  const primaryLabel = hasPlacement
    ? t('welcomeTrial.ctaRecommended')
    : t('welcomeTrial.ctaPlacement');

  return (
    <div className="welcome-trial__backdrop" role="dialog" aria-modal="true">
      <div className="welcome-trial__card">
        <div className="welcome-trial__duck" aria-hidden="true">
          <img src="/icons/loopy_64.png" alt="" width={48} height={48} />
        </div>

        <h2 className="welcome-trial__title">{t('welcomeTrial.title')}</h2>
        <p className="welcome-trial__sub">{t('welcomeTrial.sub')}</p>

        <ul className="welcome-trial__bullets">
          <li>{t('welcomeTrial.bullet1')}</li>
          <li>{t('welcomeTrial.bullet2')}</li>
          <li>{t('welcomeTrial.bullet3')}</li>
          <li>{t('welcomeTrial.bullet4')}</li>
        </ul>

        <div className="welcome-trial__actions">
          <button
            type="button"
            className="welcome-trial__primary"
            onClick={handlePrimary}
            disabled={hasPlacement === null}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            className="welcome-trial__secondary"
            onClick={onClose}
          >
            {t('welcomeTrial.later')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTrialModal;
